/**
 * CRM Tag write helpers
 *
 * Single entry point for applying tags to CRM records. Contact and deal tags
 * share one namespace: `crm_contacts.tags` is the store and `crm_deals.tags` is
 * a mirror maintained by a database trigger.
 *
 * `origin` decides which audit events the write produces, which in turn decides
 * which `TAG_ADDED` / `TAG_REMOVED` campaigns enroll:
 *   'contact'    -> contact.tag_added / contact.tag_removed
 *   'deal'       -> the same, plus deal.tag_* for the deal that was acted on
 *   'automation' -> no events (loop-safety for the MANAGE_TAG node)
 *   'import'     -> no events (bulk loads must not enroll thousands of contacts)
 */

import { supabase, isSupabaseConfigured } from './supabase';

/** Trim, drop blanks, de-dupe — keeps new whitespace variants out of the data. */
function cleanTagValues(values) {
  const seen = new Set();
  const out = [];
  for (const raw of values || []) {
    if (typeof raw !== 'string') continue;
    const value = raw.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

/** Resolve the contact that owns a deal's tags. */
async function resolveContactId(contactId, dealId) {
  if (contactId || !dealId) return contactId || null;

  const { data } = await supabase
    .from('crm_deals')
    .select('contact_id')
    .eq('id', dealId)
    .maybeSingle();

  return data?.contact_id || null;
}

/**
 * The handful of deals with no linked contact have nowhere to store shared
 * tags, so they keep their own array. The roll-up trigger ignores them.
 */
async function applyTagsToUnlinkedDeal(dealId, addValues, removeValues) {
  const { data: deal, error } = await supabase
    .from('crm_deals')
    .select('tags')
    .eq('id', dealId)
    .single();

  if (error) throw error;

  const current = deal?.tags || [];
  const next = [...current.filter(t => !removeValues.includes(t))];
  for (const value of addValues) {
    if (!next.includes(value)) next.push(value);
  }

  const { error: updateError } = await supabase
    .from('crm_deals')
    .update({ tags: next })
    .eq('id', dealId);

  if (updateError) throw updateError;
  return next;
}

/**
 * Apply tag additions/removals to a contact (and, when the user acted on a
 * deal, to that deal so deal-scoped campaign triggers still fire).
 *
 * @param {object} params
 * @param {string} [params.contactId] - Contact UUID; resolved from dealId if omitted
 * @param {string} [params.dealId]    - Deal the user acted on, when applicable
 * @param {string[]} [params.add]
 * @param {string[]} [params.remove]
 * @param {string} [params.actorId]   - Firebase UID of the acting user
 * @param {string} [params.origin]    - Defaults to 'deal' when dealId is set
 * @returns {Promise<string[]|null>} Resulting tag list, or null when unavailable
 */
export async function applyTags({ contactId, dealId = null, add = [], remove = [], actorId = null, origin = null }) {
  if (!isSupabaseConfigured() || (!contactId && !dealId)) return null;

  const addValues = cleanTagValues(add);
  const removeValues = cleanTagValues(remove);
  if (addValues.length === 0 && removeValues.length === 0) return null;

  const resolvedContactId = await resolveContactId(contactId, dealId);
  if (!resolvedContactId) {
    return applyTagsToUnlinkedDeal(dealId, addValues, removeValues);
  }

  const { data, error } = await supabase.rpc('crm_tag_apply', {
    p_contact_id: resolvedContactId,
    p_add: addValues,
    p_remove: removeValues,
    p_actor_id: actorId,
    p_origin: origin || (dealId ? 'deal' : 'contact'),
    p_origin_deal_id: dealId,
  });

  if (error) throw error;
  return data || [];
}

/**
 * Replace a contact's tag list wholesale. Used by forms that edit tags as a
 * single multi-select; the diff against stored values happens server-side so a
 * stale client array can never wipe tags it never knew about.
 *
 * @returns {Promise<string[]|null>} Resulting tag list, or null when unavailable
 */
export async function setContactTags({ contactId, tags, actorId = null, dealId = null, origin = null }) {
  if (!isSupabaseConfigured() || !contactId) return null;

  const { data, error } = await supabase.rpc('crm_tag_set', {
    p_contact_id: contactId,
    p_tags: cleanTagValues(tags),
    p_actor_id: actorId,
    p_origin: origin || (dealId ? 'deal' : 'contact'),
    p_origin_deal_id: dealId,
  });

  if (error) throw error;
  return data || [];
}

/**
 * Apply the same tag changes to many contacts at once (bulk modals, imports).
 *
 * @returns {Promise<number>} Number of contacts actually changed
 */
export async function applyTagsBulk({ contactIds, add = [], remove = [], actorId = null, origin = 'contact' }) {
  if (!isSupabaseConfigured() || !contactIds?.length) return 0;

  const addValues = cleanTagValues(add);
  const removeValues = cleanTagValues(remove);
  if (addValues.length === 0 && removeValues.length === 0) return 0;

  const { data, error } = await supabase.rpc('crm_tag_apply_bulk', {
    p_contact_ids: contactIds,
    p_add: addValues,
    p_remove: removeValues,
    p_actor_id: actorId,
    p_origin: origin,
  });

  if (error) throw error;
  return data || 0;
}

/**
 * Bulk variant addressed by deal id. Resolves each deal to its contact and
 * emits deal-scoped events for the selected deals, matching what a user gets
 * when tagging deals one at a time.
 *
 * @returns {Promise<number>} Number of deals processed
 */
export async function applyTagsBulkByDeal({ dealIds, add = [], remove = [], actorId = null }) {
  if (!isSupabaseConfigured() || !dealIds?.length) return 0;

  const addValues = cleanTagValues(add);
  const removeValues = cleanTagValues(remove);
  if (addValues.length === 0 && removeValues.length === 0) return 0;

  const { data, error } = await supabase.rpc('crm_tag_apply_bulk_by_deal', {
    p_deal_ids: dealIds,
    p_add: addValues,
    p_remove: removeValues,
    p_actor_id: actorId,
  });

  if (error) throw error;
  return data || 0;
}

/**
 * Add a single tag to a deal and its linked contact (idempotent).
 *
 * Retained for the "Move to Nurture" flow, whose nurture tags drive
 * deal-scoped TAG_ADDED campaigns.
 *
 * @param {string} dealId    - Deal UUID (optional if contactId provided)
 * @param {string} contactId - Contact UUID (optional if dealId provided)
 * @param {string} tagValue  - Tag to add (e.g. 'va-nurture')
 * @param {string} ownerId   - Firebase UID used for registration/ownership
 * @param {{ label?: string, color?: string }} [options] - Friendly label/colour
 *   applied only if the tag was auto-registered and never customised
 * @returns {Promise<{ dealTags: string[]|null, contactTags: string[]|null }>}
 */
export async function addTagToDealAndContact(dealId, contactId, tagValue, ownerId, options = {}) {
  if (!isSupabaseConfigured() || !tagValue || (!dealId && !contactId)) {
    return { dealTags: null, contactTags: null };
  }

  const tags = await applyTags({
    contactId,
    dealId,
    add: [tagValue],
    actorId: ownerId,
  });

  await applyDefaultTagPresentation(tagValue, options);

  // Contact and deal tags are the same set once the mirror trigger settles.
  return { dealTags: dealId ? tags : null, contactTags: tags };
}

/**
 * Give an auto-registered tag a friendlier label/colour.
 *
 * Scoped to rows still carrying the auto-registration defaults (label equal to
 * the value, colour gray) so it can never overwrite an admin's choice.
 */
async function applyDefaultTagPresentation(tagValue, { label, color } = {}) {
  if (!label && !color) return;

  try {
    const patch = {};
    if (label) patch.label = label;
    if (color) patch.color = color;

    await supabase
      .from('crm_tags')
      .update(patch)
      .eq('value', tagValue)
      .eq('label', tagValue)
      .eq('color', 'gray');
  } catch (err) {
    console.warn('[crmTags] could not set tag presentation (non-critical):', err);
  }
}
